import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, MousePointer, Reply } from 'lucide-react';
import React from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Connects to the SSE tracking endpoint and:
 * 1. Shows toast notifications for open/click/reply events
 * 2. Invalidates React Query cache so tracking data refreshes
 */
export function useTrackingSSE() {
    const { token } = useAuth();
    const queryClient = useQueryClient();
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    const connect = useCallback(() => {
        if (!token || eventSourceRef.current) return;

        const url = `${API}/sse/tracking?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        eventSourceRef.current = es;

        es.onopen = () => {
            // Connected successfully — clear any reconnect timer
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        es.onerror = () => {
            // Close and schedule reconnect
            es.close();
            eventSourceRef.current = null;
            reconnectTimeoutRef.current = setTimeout(connect, 5000);
        };

        // Email opened (SSE event name: "email_open" to avoid collision with EventSource built-in "open")
        es.addEventListener('email_open', (e) => {
            try {
                const data = JSON.parse(e.data);
                toast(
                    React.createElement('div', { className: 'flex items-center gap-2' },
                        React.createElement(Eye, { className: 'w-4 h-4 text-violet-600 flex-shrink-0' }),
                        React.createElement('span', null,
                            React.createElement('strong', null, data.to_email),
                            ' opened: ',
                            data.subject || 'your email'
                        )
                    ),
                    { duration: 5000 }
                );
                queryClient.invalidateQueries({ queryKey: ['tracking'] });
            } catch { /* ignore parse errors */ }
        });

        // Link clicked
        es.addEventListener('email_click', (e) => {
            try {
                const data = JSON.parse(e.data);
                toast(
                    React.createElement('div', { className: 'flex items-center gap-2' },
                        React.createElement(MousePointer, { className: 'w-4 h-4 text-blue-600 flex-shrink-0' }),
                        React.createElement('span', null,
                            React.createElement('strong', null, data.to_email),
                            ' clicked a link in: ',
                            data.subject || 'your email'
                        )
                    ),
                    { duration: 5000 }
                );
                queryClient.invalidateQueries({ queryKey: ['tracking'] });
            } catch { /* ignore */ }
        });

        // Reply detected
        es.addEventListener('email_reply', (e) => {
            try {
                const data = JSON.parse(e.data);
                toast(
                    React.createElement('div', { className: 'flex items-center gap-2' },
                        React.createElement(Reply, { className: 'w-4 h-4 text-green-600 flex-shrink-0' }),
                        React.createElement('span', null,
                            React.createElement('strong', null, data.to_email),
                            ' replied to: ',
                            data.subject || 'your email'
                        )
                    ),
                    { duration: 5000 }
                );
                queryClient.invalidateQueries({ queryKey: ['tracking'] });
                queryClient.invalidateQueries({ queryKey: ['gmail'] });
            } catch { /* ignore */ }
        });
    }, [token, queryClient]);

    useEffect(() => {
        connect();
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [connect]);
}
